import { useEffect, useState, useRef } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { useAuth } from "@/contexts/AuthContext";

export default function SessionTimeoutModal() {
  const { logout } = useAuth(); // use logout from context

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ⏱️ Total inactivity time: 40s
  const INACTIVITY_LIMIT = 40 * 1000;
  // ⏳ Countdown duration before logout: 10s
  const WARNING_DURATION = 10 * 1000;

  const resetTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    setShowWarning(false);
    setCountdown(10);

    // Start warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(10);

      // Start countdown interval safely
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }, INACTIVITY_LIMIT - WARNING_DURATION);
  };

  // ✅ Trigger logout safely when countdown hits 0
  useEffect(() => {
    if (showWarning && countdown === 0) {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      logout(); // call context logout
    }
  }, [countdown, showWarning, logout]);

  // Track user activity to reset timers
  useEffect(() => {
    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    const handleUserActivity = () => resetTimers();

    activityEvents.forEach((e) => window.addEventListener(e, handleUserActivity));
    resetTimers(); // start timers initially

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, handleUserActivity));
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  return (
    <Modal open={showWarning}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          p: 4,
          borderRadius: 2,
          boxShadow: 24,
          width: 350,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" mb={2}>
          You are about to be logged out
        </Typography>
        <Typography variant="body2">
          Your session will expire in{" "}
          <span style={{ fontWeight: "bold", color: "red" }}>{countdown}</span> seconds.
        </Typography>
      </Box>
    </Modal>
  );
}
